/** Tiny fetch wrapper: same-origin cookies + CSRF header + JSON handling. */

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    public requiresAuth?: boolean,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[2]) : null;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

/** Full URL to start the server-side Google OAuth flow (a real navigation, not fetch). */
export function googleSignInUrl(next?: string | null): string {
  const q = next ? `?next=${encodeURIComponent(next)}` : '';
  return `${API_BASE}/api/auth/google${q}`;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET' && method !== 'HEAD') {
    const csrf = readCookie('lgp.csrf');
    if (csrf) headers['x-csrf-token'] = csrf;
  }

  const res = await fetch(`${API_BASE}/api${path}`, {
    method,
    headers,
    credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  let data: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    const d = (data ?? {}) as { error?: string; code?: string; requiresAuth?: boolean };
    throw new ApiError(
      res.status,
      d.error ?? `Request failed (${res.status})`,
      d.code,
      d.requiresAuth,
    );
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};
